import { CodProofPicker, useProofPicker } from './CodProofPicker';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert } from '@/design-system/alert';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Checkbox } from '@/design-system/checkbox';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Label } from '@/design-system/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import {
  bulkConfirmCod,
  bulkConfirmCodDeposit,
  COD_DEPOSIT_METHOD_LABELS,
  COD_SETTLEMENT_SOURCES,
  COD_SETTLEMENT_SOURCE_HINTS,
  COD_SETTLEMENT_SOURCE_LABELS,
  type CodBucket,
  type CodOrderRow,
  type CodReconRiderRow,
  type CodSettlementSource,
  deliveryDetailUrl,
  getCodOrders,
  hasLeg1Shortfall,
  requiredDepositTotal,
} from '@/services/api/codOrders';
import { formatRupiah, formatTimeInStatus } from './CodBadges';

/**
 * Bulk confirm, ONE MODAL PER CUSTODY LEG.
 *
 * Both legs used to live in a single modal with a section each. That satisfied
 * the rule that a selection can never span two legs (§2), but it put two
 * independent forms and two submit buttons on one surface — so the operator had
 * to keep track of which button applied to what they had just ticked. Splitting
 * them removes that question instead of mitigating it: the stage is chosen
 * before the form opens, from a button on the rider's row, and each modal is a
 * single form with a single action.
 *
 * Each modal fetches only its OWN stage, so it cannot show an order that the
 * other leg would claim — and it re-fetches on open, which matters because bulk
 * is all-or-nothing: a stale row is exactly what gets the whole batch refused.
 *
 * docs/cod-reconciliation/bulk-reconciliation-plan.md §2, §11.
 */

/** The filters the table is under. Every bulk surface takes them, so no two
 *  surfaces can show a different set of the same rider's orders. */
export interface CodReconScope {
  clientId?: number;
  search?: string;
  /** Completion-date range — the board's only date filter. */
  completedStartDate?: string;
  completedEndDate?: string;
}

/* ---------------------------------------------------------------- *
 * Shared internals                                                  *
 * ---------------------------------------------------------------- */

/**
 * One rider's orders at one stage, UNDER THE SAME SCOPE AS THE TABLE.
 *
 * The scope is not optional. Without it the modal fetched every order for the
 * rider while the row it was opened from was date-filtered, so a row reading
 * "2 pesanan · Rp 7.430.394" opened a form offering three — and the panel's
 * one selling point is that its figures tie back to the board's cards. An
 * operator who wants to settle everything widens the range; the modal does not
 * get to decide that for them.
 */
const useRiderOrders = (
  riderId: number | null,
  bucket: CodBucket,
  scope: CodReconScope
) => {
  const [rows, setRows] = useState<CodOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { clientId, search, completedStartDate, completedEndDate } = scope;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getCodOrders({
      riderId: riderId ?? undefined,
      bucket,
      clientId,
      search,
      completedStartDate,
      completedEndDate,
      limit: 200,
    })
      .then((res) => {
        if (!cancelled) setRows(res.rows);
      })
      .catch((error: Error) => {
        if (!cancelled) toast.error(error.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [riderId, bucket, clientId, search, completedStartDate, completedEndDate]);

  return { rows, loading };
};

/** Selection state. Deliberately NOT in the URL: a half-made selection is not
 *  a view worth linking to, and a stale link would re-tick orders that may
 *  have been settled since. */
const useSelection = (rows: CodOrderRow[]) => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  // Drop ids that are no longer on offer, so a refetch cannot leave the
  // selection pointing at an order settled elsewhere in the meantime.
  useEffect(() => {
    setSelected((prev) => {
      const live = new Set(rows.map((r) => r.id));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (all: boolean) =>
      setSelected(all ? new Set(rows.map((r) => r.id)) : new Set()),
    [rows]
  );

  const rowsSelected = useMemo(
    () => rows.filter((r) => selected.has(r.id)),
    [rows, selected]
  );

  return { selected, toggle, toggleAll, rowsSelected };
};

/** Shared proof upload — one file for the whole batch (R5). */

/**
 * The order's PROVIDER ORDER ID — the client's own reference (`QA-COD-003`),
 * which is what ops recognises — linked to the DELIVERY detail by its uid.
 *
 * Two different identifiers, and the distinction matters: the label is the
 * client's reference, the address is Dash's delivery uid. Showing the uid
 * instead would be correct and useless, since nobody looks orders up by it.
 *
 * Null uid means the shipment never reached delivery-service, so there is no
 * page to open — plain text rather than a link that 404s.
 *
 * NOTHING HERE TRUNCATES. It used to be `truncate`, which silently ate the
 * tail of a long provider reference — and the tail is where these ids differ
 * (`…B2N8R5`). An identifier that is cut off cannot be matched against a
 * physical package or a client's spreadsheet, so it may as well be absent.
 * `break-words` spends a second line instead, which this surface can afford.
 */
export const OrderIdLink: React.FC<{ row: CodOrderRow }> = ({ row }) => {
  const href = useCodDetailHref(row.id);
  return (
    <Link
      to={href}
      // The row is a label wrapping a checkbox, so a bare click anywhere in it
      // toggles selection. Without this, opening the detail would also tick the
      // order into the batch.
      onClick={(event) => event.stopPropagation()}
      className="break-words text-sm leading-tight text-accent hover:text-accent-dark hover:underline"
    >
      {row.bookingId}
    </Link>
  );
};

/**
 * A link into this page's own COD detail for one order.
 *
 * Same-page navigation, not a new tab: the reader is already reconciling this
 * rider's money and the detail is the next step in that job, not a side trip.
 *
 * The current query is CARRIED, with `view` and `order` overwritten. Dropping
 * it (a bare `?order=…` href replaces the whole query string) would silently
 * discard the client and date filters the reader is working under, so coming
 * back with Back would land them on an unfiltered board.
 */
const useCodDetailHref = (shipmentId: string): string => {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  // The detail lives on the board, so the link has to land there.
  next.delete('view');
  next.set('order', shipmentId);
  return `?${next.toString()}`;
};

/**
 * The delivery-service delivery page for this order, on the uid line.
 *
 * The provider order id above used to point here; it now opens the COD detail,
 * which is what a reader on this surface almost always wants. The delivery page
 * is still one click away, and the uid is the right label to hang it on — it is
 * the key that page and support are both keyed by.
 */
const DeliveryUidLink: React.FC<{ row: CodOrderRow }> = ({ row }) => {
  const url = deliveryDetailUrl(row.deliveryUid);
  const text = row.deliveryUid ?? '—';
  const className =
    'break-words font-mono text-[11px] leading-tight text-text-sub-600';

  if (!url) return <span className={className}>{text}</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
      className={`${className} hover:text-accent hover:underline`}
    >
      {text}
    </a>
  );
};

/**
 * BOTH identifiers, stacked — provider order id over delivery uid.
 *
 * They are one column because they name one thing, and they are two lines
 * because they are two different strings that must both be readable in full:
 * ops matches the provider reference against the client's list, and the
 * delivery uid is what the delivery page (and support) is keyed by. Fitting
 * them on one line means truncating one of them, and a half-printed id is
 * worse than a wrapped one — it looks complete while being wrong.
 */
export const OrderIdCell: React.FC<{ row: CodOrderRow }> = ({ row }) => (
  <span className="flex min-w-0 flex-col gap-0.5">
    <OrderIdLink row={row} />
    <DeliveryUidLink row={row} />
  </span>
);

/**
 * Leg 1's confirmed amount, derived. The row carries the ORIGINAL COD plus the
 * signed Leg 1 discrepancy, so what the rider actually handed over is the two
 * added together — a negative discrepancy means they were short.
 */
export const leg1HandedOver = (row: CodOrderRow): number =>
  row.amount + (row.discrepancyLeg1 ?? 0);

/* ---------------------------------------------------------------- *
 * The member table                                                  *
 * ---------------------------------------------------------------- */

/**
 * Column geometry, declared once so the header and the rows cannot drift.
 *
 * Fixed widths with the SLACK AT THE END, not in the middle. An earlier version
 * let the id column take `flex-1`, which on a wide surface parked the order id
 * and its amount at opposite edges with 300px of nothing between them — the two
 * numbers you have to read together, as far apart as the layout allows. Here
 * the id, the client and the money sit adjacent and the status column absorbs
 * whatever is left.
 */
const COL = {
  check: 'w-9 shrink-0',
  order: 'w-[300px] shrink-0',
  client: 'w-32 shrink-0',
  /** `whitespace-nowrap` is the point: money never wraps and never truncates.
   *  Rp 1.248.771 broken across two lines reads as two figures. */
  amount: 'w-40 shrink-0 text-right tabular-nums whitespace-nowrap',
  status: 'min-w-[150px] flex-1',
  age: 'w-16 shrink-0 text-right',
};

/**
 * Where this order's money stands, per leg.
 *
 * At Leg 1 nothing has been measured yet — the rider still holds the cash — so
 * the honest cell is "menunggu rider", not a clean-looking "utuh". At Leg 2 the
 * comparison against Leg 1 exists, so the badge can name the actual gap.
 */
const MemberStatus: React.FC<{ row: CodOrderRow; showLeg1Gap?: boolean }> = ({
  row,
  showLeg1Gap,
}) => {
  if (!showLeg1Gap) {
    return (
      <Badge status="faded" className="text-xs">
        Menunggu rider
      </Badge>
    );
  }
  const gap = row.discrepancyLeg1 ?? 0;
  if (gap === 0) {
    return (
      <Badge status="success" className="text-xs">
        Utuh
      </Badge>
    );
  }
  return (
    <span className="flex flex-col gap-0.5">
      <Badge status="warning" className="w-fit text-xs">
        {gap < 0 ? 'Kurang' : 'Lebih'} {formatRupiah(Math.abs(gap))}
      </Badge>
      {/* The figure behind the badge, spelled out rather than hidden in a
          tooltip: a shortfall changes what Leg 2 is allowed to deposit, so
          "what did the rider actually hand over" is not hover-worthy detail. */}
      <span className="whitespace-nowrap text-[11px] leading-tight text-text-sub-600">
        rider serahkan {formatRupiah(leg1HandedOver(row))}
      </span>
    </span>
  );
};

/**
 * The selectable orders in a leg form.
 *
 * THE HEADER ROW IS THE POINT. Without it the money column was an unnamed
 * number, and on Leg 2 that number could differ from both the section total and
 * the figure in the footer — the operator had no column that added up to what
 * they were about to confirm. `amountLabel` names it; `amountOf` lets Leg 2 make
 * the column react to who covers the shortfall, so it always sums to the total
 * below it.
 *
 * There is NO editable cell here. The prototype put a `diterima` input on every
 * row and computed a live `selisih` beside it — but a bulk batch is exact-only
 * and all-or-nothing, so every value that box could hold other than the one
 * already shown is a value the server must refuse. Off-baseline orders are
 * confirmed one at a time, from the order list, where the discrepancy note that
 * Finance reads is a required field.
 */
export const MemberList: React.FC<{
  rows: CodOrderRow[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (all: boolean) => void;
  /** Names the money column: `Diterima` on Leg 1, `Disetor` on Leg 2. */
  amountLabel: string;
  /** This row's figure under the current choice. Defaults to the original COD,
   *  which is what Leg 1 always confirms. */
  amountOf?: (row: CodOrderRow) => number;
  /** Compare against Leg 1. Leg 2 only — at Leg 1 there is nothing yet to
   *  compare against. */
  showLeg1Gap?: boolean;
}> = ({
  rows,
  selected,
  onToggle,
  onToggleAll,
  amountLabel,
  amountOf,
  showLeg1Gap = false,
}) => {
  const allChecked = rows.length > 0 && selected.size === rows.length;

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-stroke-soft-200">
      <label className="flex cursor-pointer items-center gap-3 border-b border-stroke-soft-200 bg-bg-weak-50 px-3 py-2">
        <span className={COL.check}>
          <Checkbox
            checked={allChecked}
            onCheckedChange={(next) => onToggleAll(next === true)}
          />
        </span>
        <span className={`${COL.order} text-xs font-medium text-text-sub-600`}>
          Pesanan · Delivery ID
        </span>
        <span className={`${COL.client} text-xs font-medium text-text-sub-600`}>
          Client
        </span>
        <span className={`${COL.amount} text-xs font-medium text-text-sub-600`}>
          {amountLabel}
        </span>
        <span className={`${COL.status} text-xs font-medium text-text-sub-600`}>
          Status
        </span>
        <span className={`${COL.age} text-xs font-medium text-text-sub-600`}>
          Umur
        </span>
      </label>

      {/* Capped at ~45vh so the summary band and the submit button stay in
          view: a scroll that swallows the total is how you press a money
          button without having read the figure it writes. */}
      <div className="max-h-[45vh] overflow-y-auto">
        {rows.map((row) => (
          <label
            key={row.id}
            // `items-start`, not `items-center`: the id cell is two lines, and
            // centering against it drags the amount off the line its own
            // heading sits on.
            className="flex cursor-pointer items-start gap-3 border-b border-stroke-soft-200 px-3 py-2.5 last:border-b-0 hover:bg-bg-weak-50"
          >
            <span className={`${COL.check} pt-0.5`}>
              <Checkbox
                checked={selected.has(row.id)}
                onCheckedChange={() => onToggle(row.id)}
              />
            </span>
            <span className={COL.order}>
              <OrderIdCell row={row} />
            </span>
            <span
              className={`${COL.client} break-words text-xs leading-tight text-text-sub-600`}
            >
              {row.clientName ?? '—'}
            </span>
            <span
              className={`${COL.amount} text-sm font-medium text-text-strong-950`}
            >
              {formatRupiah(amountOf ? amountOf(row) : row.amount)}
            </span>
            <span className={COL.status}>
              <MemberStatus row={row} showLeg1Gap={showLeg1Gap} />
            </span>
            <span
              className={`${COL.age} text-xs leading-tight text-text-sub-600`}
            >
              {row.stageStartedAt
                ? formatTimeInStatus(row.stageStartedAt)
                : '—'}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

/**
 * What the selection adds up to — a READOUT, not a field.
 *
 * This replaced a disabled `Input`. Disabled was already honest about the
 * figure not being a decision, but a box with a border still reads as a place
 * where a number is typed, and the first thing an operator does with a money
 * box is try to correct it. Bulk is exact-only: the total is arithmetic on the
 * ticked rows, so it is rendered as arithmetic. Removing the box also removes
 * the only place a discrepancy could be entered from a bulk form, which is the
 * rule the server enforces anyway.
 */
const SelectionSummary: React.FC<{
  count: number;
  amountLabel: string;
  amount: number;
  hint: string;
}> = ({ count, amountLabel, amount, hint }) => (
  <div className="flex flex-col gap-2 rounded-xl bg-bg-weak-50 px-4 py-3">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex flex-col">
        <span className="text-[11px] uppercase tracking-wider text-text-sub-600">
          Dipilih
        </span>
        <span className="text-lg font-medium text-text-strong-950">
          {count} pesanan
        </span>
      </div>
      <div className="flex flex-col items-end">
        <span className="text-[11px] uppercase tracking-wider text-text-sub-600">
          {amountLabel}
        </span>
        <span className="whitespace-nowrap text-xl font-medium tabular-nums text-text-strong-950">
          {formatRupiah(amount)}
        </span>
      </div>
    </div>
    <Hint>{hint}</Hint>
  </div>
);

/** Modal chrome shared by both legs: title, subtitle, and a footer whose
 *  submit carries the count — so the scope of the click is on the thing being
 *  clicked, not in a line above it. */
const LegModalShell: React.FC<{
  title: string;
  rider: CodReconRiderRow;
  loading: boolean;
  empty: boolean;
  emptyLabel: string;
  submitLabel: string;
  count: number;
  submitting: boolean;
  /** Extra reason the submit is not allowed yet — Leg 2 uses it for the
   *  now-mandatory proof, which the shell has no other way to know about. */
  blocked?: boolean;
  onSubmit: () => void;
  onClose: () => void;
  children: React.ReactNode;
}> = ({
  title,
  rider,
  loading,
  empty,
  emptyLabel,
  submitLabel,
  count,
  submitting,
  blocked = false,
  onSubmit,
  onClose,
  children,
}) => (
  <FormModal
    open
    onClose={onClose}
    title={title}
    subtitle={
      rider.riderCode
        ? `${rider.riderName ?? '—'} · ${rider.riderCode}`
        : (rider.riderName ?? 'Rider')
    }
    // A CENTERED modal, deliberately — the drawer holds information, while a
    // form that writes money is a decision, and a decision belongs in front of
    // you rather than beside the thing it acts on. Same split the PM prototype
    // uses.
    //
    // WIDE, though. At `xl` (576px) six columns did not fit, so the order id,
    // the client and the amount were all truncated — on the one surface where
    // those three have to be read together and matched against a physical
    // package. `full` with the max-width pulled back to 1024px gives every
    // column its natural width and still leaves the dialog inside the viewport
    // on a 1280px laptop.
    size="full"
    // `w-[calc(100vw-3rem)]` rather than the size's own `w-full`: capped at
    // 1024px on a desktop, but on a 1024px screen it keeps a 24px margin
    // instead of sitting edge to edge with no visible backdrop.
    className="w-[calc(100vw-3rem)] max-w-5xl"
    confirmOnClose
    footer={
      <div className="flex justify-end gap-2">
        <Button style="stroke" onClick={onClose}>
          Batal
        </Button>
        <Button
          disabled={count === 0 || submitting || loading || empty || blocked}
          onClick={onSubmit}
        >
          {submitting ? 'Menyimpan…' : `${submitLabel} · ${count} pesanan`}
        </Button>
      </div>
    }
  >
    {loading ? (
      <p className="py-8 text-center text-sm text-text-sub-600">Memuat…</p>
    ) : empty ? (
      <p className="py-8 text-center text-sm text-text-sub-600">{emptyLabel}</p>
    ) : (
      <div className="flex flex-col gap-4">{children}</div>
    )}
  </FormModal>
);

/* ---------------------------------------------------------------- *
 * Leg 1 — Rider → PIC                                               *
 * ---------------------------------------------------------------- */

export const CodBulkLeg1Modal: React.FC<{
  rider: CodReconRiderRow;
  scope: CodReconScope;
  onClose: () => void;
  onDone: () => void;
}> = ({ rider, scope, onClose, onDone }) => {
  const { rows, loading } = useRiderOrders(rider.riderId, 'outstanding', scope);
  const { selected, toggle, toggleAll, rowsSelected } = useSelection(rows);
  const proof = useProofPicker();
  const [settlementSource, setSettlementSource] =
    useState<CodSettlementSource>('rider_paid');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const total = rowsSelected.reduce((sum, r) => sum + r.amount, 0);

  const submit = async () => {
    setSubmitting(true);
    try {
      const proofFiles = await proof.upload();
      const result = await bulkConfirmCod({
        shipmentIds: rowsSelected.map((r) => r.id),
        settlementSource,
        // toFixed(2), not String(total): the server compares this against a
        // sum of `numeric(14,2)` columns, and a float printing as
        // "60000.000000001" would be rejected as a mismatch.
        receivedAmount: total.toFixed(2),
        proofFiles,
        notes: notes.trim() || null,
      });
      toast.success(
        `${result.confirmed.length} pesanan dikonfirmasi · ${formatRupiah(result.totalConfirmedAmount)}`
      );
      onDone();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegModalShell
      title="Confirm Leg 1 · Rider → PIC"
      rider={rider}
      loading={loading}
      empty={rows.length === 0}
      emptyLabel="Tidak ada pesanan yang menunggu konfirmasi rider."
      submitLabel="Confirm Leg 1"
      count={rowsSelected.length}
      submitting={submitting || proof.uploading}
      onSubmit={submit}
      onClose={onClose}
    >
      {/* THE RULE, before the list rather than after a rejection.
          Bulk has no amount field and no discrepancy note, so an operator who
          arrives expecting to adjust a figure needs to know where that is
          done — otherwise they tick the row anyway and the whole batch is
          refused for it, all-or-nothing. */}
      <Alert status="information" size="lg">
        Bulk hanya untuk jumlah yang <strong>utuh</strong>. Tiap pesanan
        dikonfirmasi tepat sebesar nominal COD-nya — tidak ada kolom yang bisa
        diubah di sini, dan satu pesanan yang tidak pas membuat seluruh batch
        ditolak. Kalau jumlah yang diterima berbeda, konfirmasi pesanan itu satu
        per satu dari daftar COD supaya selisih dan catatannya tercatat.
      </Alert>

      <MemberList
        rows={rows}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        // At Leg 1 the figure IS the original COD and there is nothing yet to
        // compare it against, so one named column is the whole story.
        amountLabel="Diterima"
      />

      <SelectionSummary
        count={rowsSelected.length}
        amountLabel="Total diterima"
        amount={total}
        hint="Jumlah dari pesanan yang dicentang. Angka ini yang dikirim ke server dan harus sama persis."
      />

      {/* Two columns now that there is width for them: the one required choice
          beside the optional note, instead of a single 1000px-wide column that
          pushes the proof field below the fold. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>Sumber pelunasan</Label>
          <Select
            value={settlementSource}
            onValueChange={(v) => setSettlementSource(v as CodSettlementSource)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COD_SETTLEMENT_SOURCES.map((source) => (
                <SelectItem key={source} value={source}>
                  {COD_SETTLEMENT_SOURCE_LABELS[source]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Hint>{COD_SETTLEMENT_SOURCE_HINTS[settlementSource]}</Hint>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Catatan (opsional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
          <Hint>Satu catatan dipakai untuk semua pesanan yang dipilih.</Hint>
        </div>
      </div>

      <CodProofPicker picker={proof} />
    </LegModalShell>
  );
};

/* ---------------------------------------------------------------- *
 * Leg 2 — PIC → Finance                                             *
 * ---------------------------------------------------------------- */

export const CodBulkLeg2Modal: React.FC<{
  rider: CodReconRiderRow;
  scope: CodReconScope;
  onClose: () => void;
  onDone: () => void;
}> = ({ rider, scope, onClose, onDone }) => {
  const { rows, loading } = useRiderOrders(
    rider.riderId,
    'pending_deposit',
    scope
  );
  const { selected, toggle, toggleAll, rowsSelected } = useSelection(rows);
  const proof = useProofPicker();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shortfallCoveredBy, setShortfallCoveredBy] =
    useState<CodSettlementSource>('rider_paid');

  // Shown ONLY when the selection actually contains a Leg 1 gap. On a clean
  // batch the picker would be a question with no consequence, and answering it
  // would stamp "someone covered this" onto rows that had nothing to cover.
  const needsSource = hasLeg1Shortfall(rowsSelected);
  const source = needsSource ? shortfallCoveredBy : null;
  const total = requiredDepositTotal(rowsSelected, source);

  const submit = async () => {
    setSubmitting(true);
    try {
      const proofFiles = await proof.upload();
      const result = await bulkConfirmCodDeposit({
        shipmentIds: rowsSelected.map((r) => r.id),
        depositMethod: 'bank_transfer',
        depositedAmount: total.toFixed(2),
        shortfallCoveredBy: source,
        proofFiles,
        notes: notes.trim() || null,
      });
      toast.success(
        `${result.confirmed.length} pesanan disetor · ${formatRupiah(result.totalConfirmedAmount)}`
      );
      onDone();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegModalShell
      title="Confirm Leg 2 · PIC → Finance"
      rider={rider}
      loading={loading}
      empty={rows.length === 0}
      emptyLabel="Tidak ada pesanan yang menunggu setoran."
      submitLabel="Confirm Leg 2"
      count={rowsSelected.length}
      submitting={submitting || proof.uploading}
      // Leg 2 needs evidence, and the shell owns the submit button.
      blocked={proof.isEmpty}
      onSubmit={submit}
      onClose={onClose}
    >
      <Alert status="information" size="lg">
        Bulk hanya untuk setoran yang <strong>utuh</strong> terhadap acuannya —
        tidak ada kolom jumlah yang bisa diubah di sini, dan satu pesanan yang
        tidak pas membuat seluruh batch ditolak. Kalau jumlah yang disetor
        berbeda dari acuan, konfirmasi pesanan itu satu per satu dari daftar
        COD.
      </Alert>

      <MemberList
        rows={rows}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        // Named AND reactive. The column shows what each member will actually
        // be deposited at under the current choice, so it sums to the figure in
        // the summary below. Before this it showed the original COD while the
        // footer could ask for the Leg 1 total, and the 25.000 difference
        // appeared nowhere on screen.
        amountLabel="Disetor"
        amountOf={(row) =>
          source === 'tribe_write_off' ? leg1HandedOver(row) : row.amount
        }
        // Per-order: whether the money survived Leg 1, and what the rider
        // actually handed over on the rows where it did not.
        showLeg1Gap
      />

      {/* Only when the selection actually carries a Leg 1 gap.
          This is NOT an amount field and not a discrepancy the operator
          invents at Leg 2 — the gap already happened upstream, and this picks
          which of the two legal totals the batch is measured against. On a
          clean batch it would be a question with no consequence, and answering
          it would stamp "someone covered this" onto rows with nothing to
          cover. */}
      {needsSource && (
        <Alert status="warning" size="lg">
          <div className="flex flex-col gap-2">
            <span>
              <strong>
                {
                  rowsSelected.filter((r) => (r.discrepancyLeg1 ?? 0) !== 0)
                    .length
                }{' '}
                pesanan yang dipilih sudah selisih di Leg 1.
              </strong>{' '}
              Selisih itu terjadi sebelum tahap ini, jadi Leg 2 tetap disetor
              tanpa selisih baru — pilih dulu terhadap acuan yang mana.
            </span>
            <div className="flex flex-col gap-1.5">
              <Label>Siapa yang menanggung selisih Leg 1?</Label>
              <Select
                value={shortfallCoveredBy}
                onValueChange={(v) =>
                  setShortfallCoveredBy(v as CodSettlementSource)
                }
              >
                <SelectTrigger className="bg-bg-white-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COD_SETTLEMENT_SOURCES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {COD_SETTLEMENT_SOURCE_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* The consequence spelled out, because the choice CHANGES the
                  figure below it — and a write-off deliberately leaves the
                  order short, the one outcome nobody should pick by accident. */}
              <Hint>
                {shortfallCoveredBy === 'tribe_write_off'
                  ? 'Selisih ditanggung perusahaan — PIC hanya menyetor yang benar-benar dipegang, dan pesanan tetap tercatat kurang.'
                  : 'Selisih ditutup, jadi PIC menyetor nominal asli pesanan dan pesanan tercatat utuh.'}
              </Hint>
            </div>
          </div>
        </Alert>
      )}

      <SelectionSummary
        count={rowsSelected.length}
        amountLabel={`Total disetor · ${COD_DEPOSIT_METHOD_LABELS.bank_transfer}`}
        amount={total}
        hint={
          needsSource
            ? source === 'tribe_write_off'
              ? 'Jumlah yang benar-benar dipegang PIC (setelah selisih Leg 1 ditanggung perusahaan).'
              : 'Nominal asli pesanan — lebih besar dari yang diserahkan rider, karena selisih Leg 1 ditutup.'
            : 'Sama dengan jumlah COD pesanan yang dipilih.'
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <CodProofPicker picker={proof} required />

        <div className="flex flex-col gap-1.5">
          <Label>Catatan (opsional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
          <Hint>Satu catatan dipakai untuk semua pesanan yang dipilih.</Hint>
        </div>
      </div>
    </LegModalShell>
  );
};
